import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConnectorsAPI } from "../api/connectors";
import type {
  CallingCode,
  Connector,
  ConnectorStatus,
  ConnectorType,
  CreateConnectorData,
  HeyupNumbers,
  UpdateConnectorData,
} from "../types";

/**
 * Centralized query keys for connectors
 */
export const connectorKeys = {
  all: ["sessions_connectors"] as const,
  lists: () => [...connectorKeys.all, "list"] as const,
  list: (
    clientId: string,
    filters?: { type?: ConnectorType; status?: ConnectorStatus },
  ) => [...connectorKeys.lists(), clientId, filters] as const,
  details: () => [...connectorKeys.all, "detail"] as const,
  detail: (clientId: string, connectorId: string) =>
    [...connectorKeys.details(), clientId, connectorId] as const,
  heyupNumbers: (clientId: string) =>
    [...connectorKeys.all, "heyup_numbers", clientId] as const,
  callingCodes: (clientId: string) =>
    [...connectorKeys.all, "calling_codes", clientId] as const,
};

/**
 * The calling-code table behind the outbound-country picker. A reference list that only
 * changes when a country does, so it is fetched once and kept for the session.
 */
export function useCallingCodes(clientId: string) {
  return useQuery<CallingCode[]>({
    queryKey: connectorKeys.callingCodes(clientId),
    queryFn: () => ConnectorsAPI.getCallingCodes(clientId),
    enabled: !!clientId,
    staleTime: Infinity,
  });
}

/**
 * Get all connectors for a client
 * Optional filters for type and status
 */
export function useConnectors(
  clientId: string,
  filters?: { type?: ConnectorType; status?: ConnectorStatus },
) {
  return useQuery<Connector[]>({
    queryKey: connectorKeys.list(clientId, filters),
    queryFn: () => ConnectorsAPI.getConnectors(clientId, filters),
    enabled: !!clientId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * The HeyUp numbers this account may put on a "Numéro HeyUp" channel — the ones
 * we attributed to it on its own number requests.
 */
export function useHeyupNumbers(clientId: string) {
  return useQuery<HeyupNumbers>({
    queryKey: connectorKeys.heyupNumbers(clientId),
    queryFn: () => ConnectorsAPI.getHeyupNumbers(clientId),
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}

/**
 * Get a specific connector by ID
 */
export function useConnector(clientId: string, connectorId: string) {
  return useQuery<Connector>({
    queryKey: connectorKeys.detail(clientId, connectorId),
    queryFn: () => ConnectorsAPI.getConnector(clientId, connectorId),
    enabled: !!clientId && !!connectorId,
    staleTime: 30 * 1000,
  });
}

/**
 * Create a new connector
 */
export function useCreateConnector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      data,
    }: {
      clientId: string;
      data: CreateConnectorData;
    }) => ConnectorsAPI.createConnector(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: connectorKeys.lists(),
      });
    },
  });
}

/**
 * Update a connector (full replacement)
 */
export function useUpdateConnector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      connectorId,
      data,
    }: {
      clientId: string;
      connectorId: string;
      data: UpdateConnectorData;
    }) => ConnectorsAPI.updateConnector(clientId, connectorId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: connectorKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: connectorKeys.detail(
          variables.clientId,
          variables.connectorId,
        ),
      });
    },
  });
}

/**
 * Patch a connector (partial update)
 */
export function usePatchConnector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      connectorId,
      data,
    }: {
      clientId: string;
      connectorId: string;
      data: Partial<UpdateConnectorData>;
    }) => ConnectorsAPI.patchConnector(clientId, connectorId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: connectorKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: connectorKeys.detail(
          variables.clientId,
          variables.connectorId,
        ),
      });
    },
  });
}

/**
 * Delete a connector
 */
export function useDeleteConnector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      connectorId,
    }: {
      clientId: string;
      connectorId: string;
    }) => ConnectorsAPI.deleteConnector(clientId, connectorId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: connectorKeys.lists(),
      });
    },
  });
}

/**
 * Test a connector's configuration
 */
export function useTestConnector() {
  return useMutation({
    mutationFn: ({
      clientId,
      connectorId,
    }: {
      clientId: string;
      connectorId: string;
    }) => ConnectorsAPI.testConnector(clientId, connectorId),
  });
}
